import { gql, useMutation, useQuery } from '@apollo/client';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import IconOverflowSolid from '@apollo/icons/default/IconOverflowSolid.svg';
import IconPlus from '@apollo/icons/default/IconPlus.svg';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import IconStar from '@apollo/icons/default/IconStar.svg';
import IconStarSolid from '@apollo/icons/default/IconStarSolid.svg';
import IconChevronRight from '@apollo/icons/small/IconChevronRight.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Button,
  IconButton,
  List,
  ListIcon,
  ListItem,
  ListLabel,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { captureException } from '@sentry/react';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { LocationDescriptor } from 'history';
import produce from 'immer';
import { groupBy, orderBy, partition, sortBy } from 'lodash';
import React, { HTMLAttributes, useMemo } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { OutlinkForEmbed } from 'src/app/embedHelpers/OutlinkForEmbed';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TruncateWithTooltip } from 'src/components/TruncateWithTooltip';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePerKeyLocalStorage } from 'src/hooks/usePerKeyLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { locationToPath } from 'src/lib/routeConfig/locationToPath';
import { getStudioLinkFromEmbed } from 'src/lib/routers/embedded';
import { mergeQueryParams, setOverlayVisibleLocation } from 'src/lib/routing';
import { scrollToElementIfNotInView } from 'src/lib/scrollTo';

import { GraphRef } from '../../hooks/useGraphRef';
import { useGraphVisibility } from '../../hooks/useGraphVisibility';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { useIsLoggedIn } from '../../hooks/useIsLoggedIn';
import { explorerRouteConfig, sandboxGraphRouteConfig } from '../../routes';
import { StickyHeader } from '../components/stickyHeader/StickyHeader';
import { ExplorerDocsTab } from '../helpers/ExplorerDocsTab';
import { useCopyLinkToOperation } from '../hooks/useCopyLinkToOperation';
import {
  EditorTabState,
  useTabbedSavedOperationLocalState,
} from '../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { useHandleOpenSavedOperation } from '../hooks/useHandleOpenSavedOperation';

import {
  CollectionSortableContext,
  CollectionsDndContext,
  printDroppableId,
} from './operationCollections/collectionsDraggingHelpers';
import { OperationCollectionsLogInPrompt } from './OperationCollectionsLogInPrompt';

function parseGraphRefFromSelect(graphRefString: string) {
  const [graphId, graphVariant] = graphRefString.split(' / ');
  if (graphVariant === undefined) {
    const err = new Error('missing graphVariant when parsing select value');
    if (process.env.NODE_ENV === 'production') {
      captureException(err);
    } else {
      throw err;
    }
  }
  return {
    graphId,
    graphVariant: graphVariant ?? Config.service.defaultVariant,
  };
}
function printGraphRefForSelect(graphRef: GraphRef) {
  return `${graphRef.graphId} / ${graphRef.graphVariant}`;
}

export const SANDBOX_SELECT_ID = 'sandbox';

const CollectionEntryOperationCollectionEntryFragment = gql`
  fragment CollectionEntryOperationCollectionEntryFragment on OperationCollectionEntry {
    id
    name
    orderingIndex
    createdBy {
      id
      name
    }
  }
`;
const CollectionEntryOperationCollectionFragment = gql`
  fragment CollectionEntryOperationCollectionFragment on OperationCollection {
    id
    name
    description
    isSandbox
    isShared
    isFavorite
    permissions {
      canManage
      canEditOperations
    }
    operations {
      id
      ...CollectionEntryOperationCollectionEntryFragment
    }
    variants {
      id
    }
  }

  ${CollectionEntryOperationCollectionEntryFragment}
`;

const CollectionEntryDrag = ({
  collectionName,
  dropCursorStyle,
}: {
  collectionName: string;
  dropCursorStyle: 'not-allowed' | 'grabbing' | 'copy';
}) => (
  <Button
    className={classnames({
      'cursor-not-allowed': dropCursorStyle === 'not-allowed',
      'cursor-grabbing': dropCursorStyle === 'grabbing',
      'cursor-copy': dropCursorStyle === 'copy',
    })}
    size="sm"
    variant="hidden"
  >
    <TruncateWithTooltip content={collectionName} />
  </Button>
);

const CollectionEntry = React.memo(
  ({
    collectionEntry,
    collection,
    handleOpenSavedOperation,
    isCollectionEntrySelected,
    setSelectedCollectionEntryRow,
    setNodeRef,
    draggableRootNodeProps,
    selectedGraphRef,
    extraPadding,
  }: {
    collectionEntry: GraphQLTypes.CollectionEntryOperationCollectionFragment['operations'][number];
    collection: GraphQLTypes.CollectionEntryOperationCollectionFragment;
    handleOpenSavedOperation: ReturnType<
      typeof useHandleOpenSavedOperation
    >['handleOpenSavedOperation'];
    isCollectionEntrySelected: boolean;
    setSelectedCollectionEntryRow: React.Dispatch<
      React.SetStateAction<HTMLLIElement | null>
    >;
    isCurrentlyDragging: boolean;
    isDragging: boolean;
    setNodeRef: (node: HTMLElement | null) => void;
    draggableRootNodeProps: HTMLAttributes<HTMLLIElement>;
    selectedGraphRef: GraphRef | undefined;
    extraPadding: boolean;
  }) => {
    const trackCustomEvent = useTrackCustomEvent();
    const location = useLocation();

    const [copyLoading, setCopyLoading] = React.useState(false);
    const copyLinkToOperation = useCopyLinkToOperation();

    const [showCopiedOperationTooltip, setShowCopiedOperationTooltip] =
      React.useState(false);

    const combinedRef = React.useCallback(
      (item: HTMLLIElement | null) => {
        setNodeRef(item);
        if (isCollectionEntrySelected) {
          setSelectedCollectionEntryRow(item);
        }
      },
      [isCollectionEntrySelected, setNodeRef, setSelectedCollectionEntryRow],
    );

    const graphVisibilityType = useGraphVisibility();
    const isPublicRoute = graphVisibilityType === 'public';
    const hasPermissionToEdit =
      collection.permissions.canEditOperations && !isPublicRoute;

    return (
      <ListItem
        data-testid="operation_collection_entry"
        className={classnames(
          'relative ml-2 pr-8',
          extraPadding ? 'ml-6' : 'ml-2',
        )}
        ref={combinedRef}
        {...draggableRootNodeProps}
        onClick={() => {
          handleOpenSavedOperation({
            collectionId: collection.id,
            collectionEntryId: collectionEntry.id,
            sharedCollectionOriginVariantRef: selectedGraphRef,
          });
          trackCustomEvent({
            action: 'open_collection_entry',
            category: 'Operation Collections',
            label: collection.isSandbox
              ? 'sandbox'
              : collection.isShared
                ? 'shared'
                : 'personal',
          });
        }}
      >
        <TruncateWithTooltip
          content={collectionEntry.name}
          className="h-6 items-center truncate font-mono text-sm"
        />
        {!hasPermissionToEdit && (
          <IconLock className="hidden group-hover:block" />
        )}
        {hasPermissionToEdit && (
          <Menu placement="bottom-end" autoSelect={false}>
            <Tooltip
              label={copyLoading ? 'Loading' : 'Copied'}
              disabled={!showCopiedOperationTooltip && !copyLoading}
            >
              <MenuButton
                as={IconButton}
                className="absolute right-1 hover:bg-btn-secondary"
                variant="hidden"
                size="sm"
                type="button"
                data-testid="operation_popover"
                onClick={(e) => e.stopPropagation()}
              >
                <IconOverflowSolid />
              </MenuButton>
            </Tooltip>
            <MenuContent>
              <MenuList>
                <MenuItem
                  as={Link}
                  data-analytics-category="Operation Collections"
                  data-analytics-label="open-rename-collection-entry-modal"
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.renameOperationCollectionEntry,
                    additionalState: {
                      collectionId: collection.id,
                      collectionEntryId: collectionEntry.id,
                    },
                  })}
                >
                  Rename operation
                </MenuItem>
                {collection.isShared && (
                  <MenuItem
                    data-analytics-category="Operation Collections"
                    data-analytics-label="copy-link-to-operation"
                    onClick={async () => {
                      setCopyLoading(true);
                      await copyLinkToOperation({
                        type: 'saved',
                        savedCollectionId: collection.id,
                        savedCollectionEntryId: collectionEntry.id,
                        sharedCollectionOriginVariantRef: selectedGraphRef,
                        isShared: collection.isShared,
                      });
                      setCopyLoading(false);
                      setShowCopiedOperationTooltip(true);
                      setTimeout(() => {
                        setShowCopiedOperationTooltip(false);
                      }, 3000);
                    }}
                  >
                    Copy link to operation
                  </MenuItem>
                )}
                {collection.isShared && (
                  <MenuItem
                    as={Link}
                    data-analytics-category="Operation Collections"
                    data-analytics-label="show-operation-details"
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.viewCollectionEntryDetails,
                      additionalState: {
                        collectionId: collection.id,
                        collectionEntryId: collectionEntry.id,
                      },
                    })}
                  >
                    View operation details
                  </MenuItem>
                )}
                <MenuItem
                  as={Link}
                  className="text-error hover:bg-error hover:text-error"
                  data-analytics-category="Operation Collections"
                  data-analytics-label="open-delete-collection-entry-modal"
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.deleteOperationCollectionEntry,
                    additionalState: {
                      collectionId: collection.id,
                      collectionEntryId: collectionEntry.id,
                    },
                  })}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </MenuContent>
          </Menu>
        )}
      </ListItem>
    );
  },
);
const CollectionEntryWithDraggable = ({
  id,
  disabled,
  ...props
}: Omit<
  React.ComponentProps<typeof CollectionEntry>,
  'isDragging' | 'setNodeRef' | 'draggableRootNodeProps'
> & {
  id: string;
  disabled?: boolean;
}) => {
  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled });

  // This style contains the transformation that moves the entry to where it
  // was dragged to
  const style = React.useMemo(
    () =>
      transform
        ? {
            transform: CSS.Transform.toString(transform),
            transition,
          }
        : undefined,
    [transform, transition],
  );

  const draggableRootNodeProps = React.useMemo(
    () => ({
      style,
      ...listeners,
      ...attributes,
    }),
    [style, listeners, attributes],
  );

  return (
    <CollectionEntry
      {...props}
      isDragging={isDragging}
      setNodeRef={setNodeRef}
      draggableRootNodeProps={draggableRootNodeProps}
    />
  );
};

function BaseCollection({
  collection,
  selectedCollectionEntryIdIfInCollection,
  handleOpenSavedOperation,
  shouldFocusCollection,
  isCurrentlyDragging,
  isOver,
  selectedGraphRef,
  children,
  expanded,
  setExpanded,
  extraPadding,
}: {
  className?: string;
  collection: GraphQLTypes.CollectionEntryOperationCollectionFragment;
  // Only pass the selected tab if it is in this collection in order to avoid
  // rerenders
  selectedCollectionEntryIdIfInCollection: string | undefined;
  handleOpenSavedOperation: ReturnType<
    typeof useHandleOpenSavedOperation
  >['handleOpenSavedOperation'];
  isCurrentlyDragging: boolean;
  shouldFocusCollection: boolean;
  isOver: boolean;
  selectedGraphRef: GraphRef | undefined;
  children: React.ReactNode;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  extraPadding: boolean;
}) {
  const graphVisibilityType = useGraphVisibility();

  // When entry in this collection is selected, expand collection
  React.useEffect(() => {
    if (selectedCollectionEntryIdIfInCollection) {
      setExpanded(true);
    }
  }, [collection, setExpanded, selectedCollectionEntryIdIfInCollection]);

  // When entry is selected, scroll it into view
  const [selectedCollectionEntryRow, setSelectedCollectionEntryRow] =
    React.useState<HTMLLIElement | null>(null);
  React.useEffect(() => {
    scrollToElementIfNotInView(selectedCollectionEntryRow, {
      block: 'center',
    });
  }, [selectedCollectionEntryRow]);

  // When collection is selected, scroll it into view
  const collectionNodeRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (shouldFocusCollection) {
      scrollToElementIfNotInView(collectionNodeRef.current, {
        block: 'center',
      });
    }
  }, [shouldFocusCollection]);

  const isPublicRoute = graphVisibilityType === 'public';
  const hasPermissionToEdit =
    collection.permissions.canEditOperations && !isPublicRoute;

  return (
    <div
      className={classnames(
        'mt-2 rounded',
        isOver && hasPermissionToEdit
          ? 'bg-btn-primary-hover'
          : isCurrentlyDragging && collection.permissions.canEditOperations
            ? 'bg-primary'
            : '',
      )}
      key={collection.id}
      ref={collectionNodeRef}
    >
      {children}
      {expanded && (
        <>
          {collection.operations.length === 0 ? (
            <div
              className={classnames(
                'text-sm text-secondary',
                extraPadding ? 'ml-10' : 'ml-6',
              )}
            >
              This collection is empty.
            </div>
          ) : (
            <CollectionSortableContext collection={collection}>
              {collection.operations.map((collectionEntry) => (
                <CollectionEntryWithDraggable
                  key={collectionEntry.id}
                  id={printDroppableId({
                    type: 'collectionEntry',
                    id: collectionEntry.id,
                  })}
                  selectedGraphRef={selectedGraphRef}
                  collectionEntry={collectionEntry}
                  collection={collection}
                  handleOpenSavedOperation={handleOpenSavedOperation}
                  isCollectionEntrySelected={
                    selectedCollectionEntryIdIfInCollection ===
                    collectionEntry.id
                  }
                  setSelectedCollectionEntryRow={setSelectedCollectionEntryRow}
                  disabled={!hasPermissionToEdit}
                  isCurrentlyDragging={isCurrentlyDragging}
                  extraPadding={extraPadding}
                />
              ))}
              <div className="h-2" />
            </CollectionSortableContext>
          )}
        </>
      )}
    </div>
  );
}
const Collection = React.memo(
  ({
    setNodeRef,
    ...props
  }: {
    setNodeRef: (element: HTMLElement | null) => void;
  } & Omit<
    React.ComponentProps<typeof BaseCollection>,
    'expanded' | 'setExpanded' | 'children' | 'extraPadding'
  >) => {
    const { collection, shouldFocusCollection, isOver, selectedGraphRef } =
      props;
    const [expanded, setExpanded] = React.useState(shouldFocusCollection);
    const location = useLocation();

    const [updateIsFavorite] = useMutation<
      GraphQLTypes.UpdateIsFavorite,
      GraphQLTypes.UpdateIsFavoriteVariables
    >(
      gql`
        mutation UpdateIsFavorite(
          $isFavorite: Boolean!
          $operationCollectionId: ID!
        ) {
          operationCollection(id: $operationCollectionId) {
            updateIsFavorite(isFavorite: $isFavorite) {
              ... on OperationCollection {
                id
                isFavorite
              }
            }
          }
        }
      `,
      {
        optimisticResponse: (variables) => ({
          operationCollection: {
            __typename: 'OperationCollectionMutation',
            updateIsFavorite: {
              __typename: 'OperationCollection',
              id: variables.operationCollectionId,
              isFavorite: variables.isFavorite,
            },
          },
        }),
      },
    );

    const { isLoggedIn } = useIsLoggedIn();

    const graphVisibilityType = useGraphVisibility();

    const isEmbedded =
      isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute();
    const [embedParentHref] = usePerKeyLocalStorage({
      initialValue: undefined,
      key: document.referrer,
      localStorageKey: 'embedParentHref',
    });

    const { locationToExplorer } = useInternalGraphLinking();
    const copyLinkToCollection = React.useCallback(
      (collectionId: string) => {
        const collectionQueryParams = {
          collectionId,
          focusCollectionId: collectionId,
        };
        // for the embeds, if we have the parent
        // href, we copy the link to the parent href & we consume the link
        // from the parent href
        if (isEmbedded && embedParentHref) {
          const parentUrl = new URL(embedParentHref);

          Object.entries({
            ...collectionQueryParams,
          }).forEach(([key, value]) => {
            if (value) {
              if (parentUrl.searchParams.has(key)) {
                parentUrl.searchParams.delete(key);
              }

              parentUrl.searchParams.append(key, value);
            }
          });
          copy(parentUrl.href);
          return;
        }
        const baseHostname = isEmbedded
          ? getStudioLinkFromEmbed('')
          : Config.absoluteUrl.replace(/\/+$/, '');

        const locationWithCollectionFocused =
          // when there is a selectedGraphRef, we want to link to it
          // instead of the route's graphRef like locationToExplorer does
          selectedGraphRef
            ? // eslint-disable-next-line local/no-direct-route-config-access
              explorerRouteConfig.location({
                ...selectedGraphRef,
                graphVisibilityType,
                ...collectionQueryParams,
              })
            : locationToExplorer({
                graphVisibilityType,
                ...collectionQueryParams,
              });

        copy(`${baseHostname}${locationToPath(locationWithCollectionFocused)}`);
      },
      [
        embedParentHref,
        graphVisibilityType,
        isEmbedded,
        locationToExplorer,
        selectedGraphRef,
      ],
    );

    const [showCopiedCollectionTooltip, setShowCopiedCollectionTooltip] =
      React.useState(false);

    const isPublicRoute = graphVisibilityType === 'public';
    const hasPermissionToEdit =
      collection.permissions.canEditOperations && !isPublicRoute;
    const hasPermissionToManage =
      collection.permissions.canManage && !isPublicRoute;

    return (
      <BaseCollection
        expanded={expanded}
        setExpanded={setExpanded}
        extraPadding={true}
        {...props}
      >
        <ListItem
          data-testid="operation_collection"
          className={classnames(
            'relative pl-2 pr-16',
            isOver && hasPermissionToEdit && 'font-semibold',
          )}
          onClick={() => setExpanded(!expanded)}
          ref={setNodeRef}
        >
          <ListIcon
            as={IconChevronRight}
            className={classnames(
              'transition-transform',
              expanded && 'rotate-90',
            )}
          />
          <Tooltip
            label="You do not have permission to edit this collection"
            disabled={!isOver || hasPermissionToEdit}
          >
            <TruncateWithTooltip content={collection.name} />
          </Tooltip>
          {!isPublicRoute && (
            <Tooltip label={collection.isFavorite ? 'Unfavorite' : 'Favorite'}>
              <IconButton
                className="absolute right-9 hover:bg-btn-secondary"
                variant="hidden"
                size="sm"
                aria-label={`Click to ${
                  collection.isFavorite ? 'unfavorite' : 'favorite'
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  updateIsFavorite({
                    variables: {
                      isFavorite: !collection.isFavorite,
                      operationCollectionId: collection.id,
                    },
                  });
                }}
                icon={collection.isFavorite ? <IconStarSolid /> : <IconStar />}
              />
            </Tooltip>
          )}

          <Menu placement="bottom-end" autoSelect={false}>
            <Tooltip label="Copied" disabled={!showCopiedCollectionTooltip}>
              <MenuButton
                className="absolute right-1 hover:bg-btn-secondary"
                as={IconButton}
                size="sm"
                variant="hidden"
                data-testid="collection_popover"
                icon={<IconOverflowSolid />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
            <MenuContent>
              <MenuList>
                {collection.isShared && (
                  <MenuItem
                    data-analytics-category="Operation Collections"
                    data-analytics-label="copy-link-to-collection"
                    onClick={() => {
                      setShowCopiedCollectionTooltip(true);
                      setTimeout(() => {
                        setShowCopiedCollectionTooltip(false);
                      }, 3000);
                      copyLinkToCollection(collection.id);
                    }}
                  >
                    Copy link to collection
                  </MenuItem>
                )}
                <MenuItem
                  as={Link}
                  data-analytics-category="Operation Collections"
                  data-analytics-label="open-view-details-modal"
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.viewCollectionDetails,
                    additionalSearchParameters: {
                      collectionId: collection.id,
                    },
                  })}
                >
                  View details
                </MenuItem>
                {hasPermissionToManage && (
                  <MenuItem
                    as={Link}
                    data-analytics-category="Operation Collections"
                    data-analytics-label="open-configure-collection-modal"
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.configureOperationCollection,
                      additionalSearchParameters: {
                        collectionId: collection.id,
                      },
                    })}
                  >
                    Configure
                  </MenuItem>
                )}
                {isLoggedIn && (
                  <MenuItem
                    as={Link}
                    data-analytics-category="Operation Collections"
                    data-analytics-label="open-duplicate-collection-modal"
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.duplicateOperationCollection,
                      additionalState: { collectionId: collection.id },
                    })}
                  >
                    Duplicate
                  </MenuItem>
                )}
                {hasPermissionToManage && (
                  <MenuItem
                    as={Link}
                    className="text-error hover:bg-error hover:text-error"
                    data-analytics-category="Operation Collections"
                    data-analytics-label="open-delete-collection-modal"
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.deleteOperationCollection,
                      additionalState: { collectionId: collection.id },
                    })}
                  >
                    Delete
                  </MenuItem>
                )}
              </MenuList>
            </MenuContent>
          </Menu>
        </ListItem>
      </BaseCollection>
    );
  },
);

const DefaultCollection = React.memo(
  ({
    description,
    setNodeRef,
    ...props
  }: {
    description: string;
    setNodeRef: (element: HTMLElement | null) => void;
  } & Omit<
    React.ComponentProps<typeof BaseCollection>,
    'expanded' | 'setExpanded' | 'children' | 'extraPadding'
  >) => {
    const [expanded, setExpanded] = React.useState(props.shouldFocusCollection);

    return (
      <BaseCollection
        expanded={expanded}
        setExpanded={setExpanded}
        extraPadding={false}
        {...props}
      >
        <ListLabel
          className="mb-3 flex items-center border-b border-primary pb-1"
          onClick={() => setExpanded(!expanded)}
          ref={setNodeRef}
        >
          <ListIcon
            as={IconChevronRight}
            className={classnames(
              'mr-2 transition-transform',
              expanded && 'rotate-90',
            )}
          />
          Default MCP Tools
          <Tooltip label={description}>
            <IconInfo className="ml-2 flex-none" />
          </Tooltip>
        </ListLabel>
      </BaseCollection>
    );
  },
);

const CollectionWithDroppable = ({
  id,
  disabled,
  ...props
}: Omit<React.ComponentProps<typeof Collection>, 'isOver' | 'setNodeRef'> & {
  id: string;
  disabled?: boolean;
}) => {
  const { isOver, setNodeRef } = useDroppable({ id, disabled });
  return <Collection {...props} isOver={isOver} setNodeRef={setNodeRef} />;
};

const DefaultCollectionsListSection = ({
  selectedCollectionEntryId,
  focusCollectionId,
  className,
  ...props
}: Omit<
  React.ComponentProps<typeof DefaultCollection>,
  | 'isOver'
  | 'setNodeRef'
  | 'selectedCollectionEntryIdIfInCollection'
  | 'shouldFocusCollection'
> & {
  selectedCollectionEntryId: string | undefined;
  focusCollectionId: string | undefined;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: printDroppableId({ type: 'collection', id: props.collection.id }),
  });
  return (
    <div className={className}>
      <DefaultCollection
        key={props.collection.id}
        selectedCollectionEntryIdIfInCollection={
          props.collection.operations.some(
            (operation) => operation.id === selectedCollectionEntryId,
          )
            ? selectedCollectionEntryId
            : undefined
        }
        isOver={isOver}
        setNodeRef={setNodeRef}
        shouldFocusCollection={props.collection.id === focusCollectionId}
        {...props}
      />
    </div>
  );
};
const CollectionsListSection = ({
  className,
  title,
  description,
  collections,
  canCreateCollections,
  creationModalLink,
  selectedCollectionEntryId,
  handleOpenSavedOperation,
  focusCollectionId,
  isCurrentlyDragging,
  shouldShowSectionHeader,
  selectedGraphRef,
}: {
  className?: string;
  title: string;
  description: string;
  collections: GraphQLTypes.CollectionEntryOperationCollectionFragment[];
  canCreateCollections: boolean;
  creationModalLink: LocationDescriptor<unknown> | null;
  selectedCollectionEntryId: string | undefined;
  handleOpenSavedOperation: ReturnType<
    typeof useHandleOpenSavedOperation
  >['handleOpenSavedOperation'];
  focusCollectionId: string | undefined;
  isCurrentlyDragging: boolean;
  shouldShowSectionHeader: boolean;
  selectedGraphRef?: GraphRef;
}) => {
  return (
    <div className={className}>
      {shouldShowSectionHeader && (
        <ListLabel className="flex items-center border-b border-primary pb-1">
          {title}
          <Tooltip label={description}>
            <IconInfo className="ml-2 flex-none" />
          </Tooltip>
          <span className="flex-1" />
          {creationModalLink && (
            <Tooltip
              label={
                canCreateCollections
                  ? 'Create collection'
                  : 'Cannot create collections in this variant'
              }
            >
              <IconButton
                aria-label="Create collection"
                isDisabled={!canCreateCollections}
                size="sm"
                variant="hidden"
                data-analytics-category="Operation Collections"
                data-analytics-label="open-create-collection-modal"
                as={Link}
                to={creationModalLink}
                icon={<IconPlus />}
              />
            </Tooltip>
          )}
        </ListLabel>
      )}
      {collections.length ? (
        orderBy(
          collections,
          [
            (collection) => collection.isFavorite,
            (collection) => collection.name,
          ],
          ['desc'],
        ).map((collection) => (
          <CollectionWithDroppable
            selectedGraphRef={selectedGraphRef}
            key={collection.id}
            id={printDroppableId({ type: 'collection', id: collection.id })}
            collection={collection}
            selectedCollectionEntryIdIfInCollection={
              collection.operations.some(
                (operation) => operation.id === selectedCollectionEntryId,
              )
                ? selectedCollectionEntryId
                : undefined
            }
            handleOpenSavedOperation={handleOpenSavedOperation}
            shouldFocusCollection={collection.id === focusCollectionId}
            isCurrentlyDragging={isCurrentlyDragging}
          />
        ))
      ) : (
        <div className="mt-3 text-sm">You have no collections.</div>
      )}
    </div>
  );
};

export const OperationCollections = ({
  graphRef,
  editorTabState,
  setDocsTab,
  dragAndDropRef,
}: {
  graphRef: GraphRef | null;
  editorTabState: EditorTabState;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  dragAndDropRef: React.RefObject<HTMLDivElement>;
}) => {
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

  const history = useHistory();
  const location = useLocation();

  const trackCustomEvent = useTrackCustomEvent();

  const [lastFocusedCollectionId, setFocusCollectionId] =
    React.useState<string>();
  const { currentExplorerRouteConfig } = useInternalGraphLinking();
  const { focusCollectionId } = useRouteParams(currentExplorerRouteConfig);
  React.useEffect(() => {
    if (focusCollectionId) {
      setFocusCollectionId(focusCollectionId);
      history.push({
        ...location,
        search: mergeQueryParams(location.search, {}, ['focusCollectionId']),
      });
      setDocsTab('Operation Collections');
    }
  }, [focusCollectionId, history, location, setDocsTab]);

  const graphVisibilityType = useGraphVisibility();
  const isPublicRoute = graphVisibilityType === 'public';
  const showMcpSection = useLDFlag('tooling-show-mcp-collection-in-explorer');

  const { data: variantsData, loading: variantsLoading } = useQuery<
    GraphQLTypes.OperationCollectionsVariantsV2Query,
    GraphQLTypes.OperationCollectionsVariantsV2QueryVariables
  >(
    gql`
      fragment CollectionsOnServiceFragment on Service {
        id
        title
        variants {
          id
          isFavoriteOfCurrentUser
          name
          operationCollectionsConnection {
            totalCount
          }
        }
      }

      query OperationCollectionsVariantsV2Query(
        $isSandbox: Boolean!
        $graphId: ID!
      ) {
        me {
          id
          ... on User {
            memberships @include(if: $isSandbox) {
              user {
                id
              }
              account {
                id
                services {
                  id
                  ...CollectionsOnServiceFragment
                }
              }
            }
          }
        }
        service(id: $graphId) @skip(if: $isSandbox) {
          id
          account {
            id
          }
          ...CollectionsOnServiceFragment
        }
      }
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        isSandbox: isSandboxRoute,
        graphId: graphRef?.graphId ?? '',
      },
    },
  );

  const availableVariants = React.useMemo(() => {
    let unorderedVariants;
    if (isSandboxRoute) {
      if (
        variantsData?.me?.__typename !== 'User' ||
        !variantsData.me.memberships
      ) {
        return undefined;
      }
      unorderedVariants = variantsData.me.memberships.flatMap((membership) =>
        membership.account.services.flatMap((graph) =>
          graph.variants.map((variant) => ({
            variantId: variant.id,
            graphId: graph.id,
            graphTitle: graph.title,
            orgId: membership.account.id,
            isFavorite: variant.isFavoriteOfCurrentUser,
            graphVariant: variant.name,
            operationCollectionsCount:
              variant.operationCollectionsConnection?.totalCount ?? 0,
          })),
        ),
      );
    } else {
      const graph = variantsData?.service;
      const account = graph?.account;
      // If you are viewing a public variant you would get
      // a service with no account, but that can't happen on
      // a sandbox route anyways
      if (!graph || !account) {
        return undefined;
      }
      unorderedVariants = graph.variants.map((variant) => ({
        variantId: variant.id,
        graphId: graph.id,
        graphTitle: graph.title,
        orgId: account.id,
        isFavorite: variant.isFavoriteOfCurrentUser,
        graphVariant: variant.name,
        operationCollectionsCount:
          variant.operationCollectionsConnection?.totalCount ?? 0,
      }));
    }
    return orderBy(
      unorderedVariants.filter(
        (variant) => variant.operationCollectionsCount > 0,
      ),
      (variant) => variant.isFavorite,
      ['desc'],
    );
  }, [isSandboxRoute, variantsData]);

  // If user is on sandbox, the variant select will control sandboxSelectorState,
  // which is a local storage backed value that powers diffs/checks as well. On
  // a registered graph the variant select will control nonSandboxSelectorState,
  // a GraphRef in React state.
  const [sandboxSelectorState, setSandboxSelectorState] = useLocalStorage(
    'sandboxCheckDiffSelectorState',
  );
  const [nonSandboxSelectorState, setNonSandboxSelectorState] = React.useState<
    GraphRef | undefined
  >(graphRef ?? undefined);

  const selectedGraphRef = useMemo(() => {
    if (isSandboxRoute) {
      const sandboxSelectorGraphVariant = sandboxSelectorState
        ? availableVariants?.find(
            (variant) => variant.variantId === sandboxSelectorState.variantId,
          )?.graphVariant
        : undefined;
      return sandboxSelectorGraphVariant && sandboxSelectorState
        ? {
            graphVariant: sandboxSelectorGraphVariant,
            graphId: sandboxSelectorState.graphId,
          }
        : undefined;
    } else {
      return nonSandboxSelectorState;
    }
  }, [
    isSandboxRoute,
    availableVariants,
    nonSandboxSelectorState,
    sandboxSelectorState,
  ]);
  const setSelectedGraphRef = React.useCallback(
    (newGraphRef: GraphRef | undefined) => {
      if (isSandboxRoute) {
        const matchingVariant = availableVariants?.find(
          (variant) =>
            variant.graphId === newGraphRef?.graphId &&
            variant.graphVariant === newGraphRef?.graphVariant,
        );
        setSandboxSelectorState(matchingVariant);
      } else {
        setNonSandboxSelectorState(newGraphRef);
      }
    },
    [availableVariants, isSandboxRoute, setSandboxSelectorState],
  );

  const {
    data: registeredCollectionData,
    loading: registeredCollectionLoading,
  } = useQuery<
    GraphQLTypes.OperationCollectionsRegisteredQuery,
    GraphQLTypes.OperationCollectionsRegisteredQueryVariables
  >(
    gql`
      query OperationCollectionsRegisteredQuery(
        $graphId: ID!
        $graphVariant: String!
        $showMcpSection: Boolean!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            mcpDefaultCollection @include(if: $showMcpSection) {
              ... on OperationCollection {
                id
                ...CollectionEntryOperationCollectionFragment
              }
            }
            permissions {
              canShareCollectionInVariant
              canCreateCollectionInVariant
            }
            operationCollections {
              id
              ...CollectionEntryOperationCollectionFragment
            }
          }
        }
      }

      ${CollectionEntryOperationCollectionFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      skip: !selectedGraphRef,
      variables: {
        ...(selectedGraphRef as NonNullable<typeof selectedGraphRef>),
        showMcpSection,
      },
    },
  );

  const { data: sandboxCollectionData, loading: sandboxCollectionLoading } =
    useQuery<
      GraphQLTypes.OperationCollectionsSandboxQuery,
      GraphQLTypes.OperationCollectionsSandboxQueryVariables
    >(
      gql`
        query OperationCollectionsSandboxQuery {
          me {
            id
            ... on User {
              sandboxOperationCollections {
                id
                ...CollectionEntryOperationCollectionFragment
              }
            }
          }
        }

        ${CollectionEntryOperationCollectionFragment}
      `,
      {
        context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
        skip: !!selectedGraphRef,
      },
    );

  const canShareCollectionInVariant = selectedGraphRef
    ? registeredCollectionData?.service?.variant?.permissions
        .canShareCollectionInVariant
    : undefined;

  const { isLoggedIn } = useIsLoggedIn();

  const mcpDefaultCollection =
    selectedGraphRef && showMcpSection
      ? registeredCollectionData?.service?.variant?.mcpDefaultCollection
      : undefined;
  const originalSandboxCollections =
    !selectedGraphRef && sandboxCollectionData?.me?.__typename === 'User'
      ? sandboxCollectionData.me?.sandboxOperationCollections
      : undefined;
  const originalVariantCollections = selectedGraphRef
    ? registeredCollectionData?.service?.variant?.operationCollections
    : undefined;
  const allCollections = React.useMemo(
    () => [
      ...(originalSandboxCollections ?? []),
      ...(originalVariantCollections ?? []),
    ],
    [originalSandboxCollections, originalVariantCollections],
  );

  const [activeDragCollectionEntryId, setActiveDragCollectionEntryId] =
    React.useState<string | null>(null);
  const isCurrentlyDragging = activeDragCollectionEntryId !== null;

  // dragOverride will be set when the collection entry is dragged over a
  // different collection. dnd-kit sortables require that to interact with the
  // entries in a list, the element must actually be in that list, so this will
  // temporarily move the element to be part of that list.
  const [dragOverride, setDragOverride] = React.useState<{
    sourceCollectionId: string;
    targetCollectionId: string;
    collectionEntryId: string;
  }>();

  const {
    originalSandboxCollections: sandboxCollections,
    originalVariantCollections: variantCollections,
  } = React.useMemo(() => {
    if (dragOverride) {
      const { collectionEntryId, sourceCollectionId, targetCollectionId } =
        dragOverride;

      return produce(
        { originalSandboxCollections, originalVariantCollections },
        ({
          originalSandboxCollections: draftSandboxCollections,
          originalVariantCollections: draftVariantCollections,
        }) => {
          const allDraftCollections = [
            ...(draftSandboxCollections ?? []),
            ...(draftVariantCollections ?? []),
          ];
          const sourceCollection = allDraftCollections.find(
            ({ id }) => id === sourceCollectionId,
          );
          const targetCollection = allDraftCollections.find(
            ({ id }) => id === targetCollectionId,
          );
          const collectionEntry = sourceCollection?.operations.find(
            ({ id }) => id === collectionEntryId,
          );
          if (!sourceCollection || !targetCollection || !collectionEntry) {
            return;
          }
          sourceCollection.operations = sourceCollection.operations.filter(
            ({ id }) => id !== collectionEntryId,
          );
          targetCollection.operations = [
            collectionEntry,
            ...targetCollection.operations,
          ];
        },
      );
    }
    return { originalSandboxCollections, originalVariantCollections };
  }, [originalSandboxCollections, originalVariantCollections, dragOverride]);

  const [variantSharedCollections, variantPersonalCollections] = useMemo(
    () =>
      partition(
        variantCollections?.filter((collection) => {
          return (
            !showMcpSection ||
            !mcpDefaultCollection ||
            mcpDefaultCollection.__typename !== 'OperationCollection' ||
            collection.id !== mcpDefaultCollection.id
          );
        }),
        (collection) => collection.isShared,
      ),
    [variantCollections, mcpDefaultCollection, showMcpSection],
  );

  const { handleOpenSavedOperation } = useHandleOpenSavedOperation(
    editorTabState.dispatchSavedOperationLocalStateAction,
  );

  const { savedOperationLocalState } = useTabbedSavedOperationLocalState();
  const selectedCollectionEntryId =
    editorTabState.selectedTab === undefined
      ? undefined
      : savedOperationLocalState[editorTabState.selectedTab]?.collectionEntryId;

  const operationCollectionsHeader = (
    <>
      <StickyHeader className="mb-4">Operation Collections</StickyHeader>
      <div className="text-sm text-secondary">
        {selectedGraphRef
          ? `Showing saved operations for ${selectedGraphRef.graphId}@${selectedGraphRef.graphVariant}`
          : 'Showing saved operations for your Sandbox, across all endpoints'}
      </div>
    </>
  );

  if (!isPublicRoute && !isLoggedIn) {
    return (
      <List isInteractive>
        {operationCollectionsHeader}
        <OperationCollectionsLogInPrompt />
      </List>
    );
  }

  return (
    <CollectionsDndContext
      allCollections={allCollections}
      activeDragCollectionEntryId={activeDragCollectionEntryId}
      setActiveDragCollectionEntryId={setActiveDragCollectionEntryId}
      setDragOverride={setDragOverride}
      dragAndDropRef={dragAndDropRef}
      CollectionEntryDrag={CollectionEntryDrag}
    >
      <List isInteractive>
        {operationCollectionsHeader}
        {!isPublicRoute &&
          availableVariants &&
          availableVariants.length !== 0 && (
            <>
              <div className="mb-3 flex justify-between">
                <Select
                  className="shadow-none"
                  size="small"
                  data-analytics-label={`Select Collections Variant - Route: ${
                    isSandboxRoute ? 'Sandbox' : 'Registered Graph'
                  }`}
                  data-analytics-category="Operation Collections"
                  onChange={(e) => {
                    const isSandboxSelected =
                      e.target.value === SANDBOX_SELECT_ID;
                    const newGraphRef = isSandboxSelected
                      ? undefined
                      : parseGraphRefFromSelect(e.target.value);
                    setSelectedGraphRef(newGraphRef);
                    trackCustomEvent({
                      action: 'switched_collections_panel_variant',
                      category: 'Operation Collections',
                      label: `Route: ${
                        isSandboxRoute ? 'Sandbox' : 'Registered Graph'
                      } - Selected: ${
                        isSandboxSelected ? 'Sandbox' : 'Registered Graph'
                      }`,
                    });
                  }}
                  renderTriggerNode={() => {
                    const selectedVariant =
                      selectedGraphRef &&
                      availableVariants.find(
                        (variant) =>
                          variant.graphId === selectedGraphRef.graphId &&
                          variant.graphVariant ===
                            selectedGraphRef.graphVariant,
                      );
                    if (selectedVariant) {
                      return (
                        <span className="flex items-center">
                          {selectedVariant.graphTitle} /{' '}
                          {selectedVariant.graphVariant}
                        </span>
                      );
                    }
                    return (
                      <span className="flex items-center">
                        <IconSandbox className="mr-1" />
                        Sandbox
                      </span>
                    );
                  }}
                  value={
                    selectedGraphRef
                      ? printGraphRefForSelect(selectedGraphRef)
                      : SANDBOX_SELECT_ID
                  }
                >
                  <option value={SANDBOX_SELECT_ID} key={SANDBOX_SELECT_ID}>
                    <div className="flex items-center font-semibold uppercase">
                      <IconSandbox className="mr-1" />
                      Sandbox
                    </div>
                  </option>
                  {sortBy(
                    Object.entries(
                      groupBy(
                        availableVariants,
                        (variants) => variants.graphId,
                      ),
                    ),
                    (graphs) => graphs[0].toLowerCase(),
                  ).map(([graphId, graphVariants]) => (
                    <optgroup
                      label={graphVariants[0]?.graphTitle}
                      key={graphId}
                    >
                      {sortBy(graphVariants, (variant) =>
                        variant.graphVariant.toLowerCase(),
                      ).map((variantRef) => {
                        const refString = printGraphRefForSelect(variantRef);
                        return (
                          <option value={refString} key={refString}>
                            {variantRef.graphTitle} / {variantRef.graphVariant}
                          </option>
                        );
                      })}
                    </optgroup>
                  ))}
                </Select>
                {selectedGraphRef && (
                  <Tooltip label="Open this variant">
                    <IconButton
                      size="sm"
                      aria-label="Open this variant"
                      variant="hidden"
                      as={OutlinkForEmbed}
                      icon={<IconOutlink />}
                      target="_blank"
                      rel="noopener noreferrer"
                      // eslint-disable-next-line local/no-direct-route-config-access
                      to={explorerRouteConfig.location({
                        ...selectedGraphRef,
                      })}
                    />
                  </Tooltip>
                )}
              </div>
            </>
          )}
        {(selectedGraphRef
          ? registeredCollectionLoading
          : sandboxCollectionLoading) || variantsLoading ? (
          <div className="mt-28">
            <Loading />
          </div>
        ) : !(sandboxCollections || variantCollections) ? (
          <div className="m-2">We could not fetch your collections.</div>
        ) : (
          <>
            {sandboxCollections && (
              <CollectionsListSection
                title="Sandbox"
                description="Only visible to you in your Sandbox"
                collections={sandboxCollections}
                canCreateCollections={true}
                creationModalLink={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.createPersonalOperationCollection,
                  additionalState: { graphRef: null },
                })}
                selectedCollectionEntryId={selectedCollectionEntryId}
                handleOpenSavedOperation={handleOpenSavedOperation}
                focusCollectionId={lastFocusedCollectionId}
                isCurrentlyDragging={isCurrentlyDragging}
                shouldShowSectionHeader={true}
                selectedGraphRef={selectedGraphRef}
              />
            )}
            {mcpDefaultCollection &&
              mcpDefaultCollection.__typename === 'OperationCollection' &&
              showMcpSection &&
              variantCollections &&
              selectedGraphRef && (
                <DefaultCollectionsListSection
                  description="Operations that the Apollo MCP Server will use as MCP tools if no collection id is specified."
                  collection={mcpDefaultCollection}
                  selectedCollectionEntryId={selectedCollectionEntryId}
                  handleOpenSavedOperation={handleOpenSavedOperation}
                  isCurrentlyDragging={isCurrentlyDragging}
                  selectedGraphRef={selectedGraphRef}
                  focusCollectionId={focusCollectionId}
                  className="mb-5"
                />
              )}
            {variantCollections && selectedGraphRef && !isPublicRoute && (
              <CollectionsListSection
                className="mb-8"
                title="Personal"
                description="Visible only to you"
                collections={variantPersonalCollections}
                canCreateCollections={true}
                creationModalLink={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.createPersonalOperationCollection,
                  additionalState: { graphRef: selectedGraphRef },
                })}
                selectedCollectionEntryId={selectedCollectionEntryId}
                handleOpenSavedOperation={handleOpenSavedOperation}
                focusCollectionId={lastFocusedCollectionId}
                isCurrentlyDragging={isCurrentlyDragging}
                shouldShowSectionHeader={true}
                selectedGraphRef={selectedGraphRef}
              />
            )}
            {variantCollections && selectedGraphRef && (
              <CollectionsListSection
                title="Shared"
                description="Shared across your organization"
                collections={variantSharedCollections}
                canCreateCollections={
                  !isPublicRoute && !!canShareCollectionInVariant
                }
                creationModalLink={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.createSharedOperationCollection,
                  additionalState: { graphRef: selectedGraphRef },
                })}
                selectedCollectionEntryId={selectedCollectionEntryId}
                handleOpenSavedOperation={handleOpenSavedOperation}
                focusCollectionId={lastFocusedCollectionId}
                isCurrentlyDragging={isCurrentlyDragging}
                shouldShowSectionHeader={!isPublicRoute}
                selectedGraphRef={selectedGraphRef}
              />
            )}
          </>
        )}
      </List>
    </CollectionsDndContext>
  );
};
